import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, SlidersHorizontal, Tag, Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { useAdvancedSearch } from "@/hooks/useAdvancedSearch";
import { SearchSuggestions, saveSearchToHistory } from "@/components/SearchSuggestions";

interface AdvancedSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AdvancedSearchDialog = ({ open, onOpenChange }: AdvancedSearchDialogProps) => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>([0, 50000]);
  
  const { 
    filters,
    results, 
    suggestions, 
    filterOptions,
    searching,
    updateFilters,
    resetFilters,
    activeFilterCount,
  } = useAdvancedSearch();

  useEffect(() => {
    if (!open) {
      resetFilters();
      setShowFilters(false);
    }
  }, [open, resetFilters]);

  useEffect(() => {
    if (filterOptions?.priceRange) {
      setLocalPriceRange([
        filterOptions.priceRange.min || 0,
        filterOptions.priceRange.max || 50000
      ]);
    }
  }, [filterOptions]);

  const handleProductClick = (slug: string) => {
    if (filters.query) {
      saveSearchToHistory(filters.query);
    }
    onOpenChange(false);
    navigate(`/product/${slug}`);
  };

  const handleSuggestionClick = (suggestion: string) => {
    updateFilters({ query: suggestion });
  };

  const handleCategoryClick = (categoryId: string) => {
    const current = filters.categories;
    if (current.includes(categoryId)) {
      updateFilters({ categories: current.filter(c => c !== categoryId) });
    } else {
      updateFilters({ categories: [...current, categoryId] });
    }
  };

  const handleBrandClick = (brandId: string) => {
    const current = filters.brands;
    if (current.includes(brandId)) {
      updateFilters({ brands: current.filter(b => b !== brandId) });
    } else {
      updateFilters({ brands: [...current, brandId] });
    }
  };

  const handleApplyPriceRange = () => {
    updateFilters({ 
      minPrice: localPriceRange[0] > 0 ? localPriceRange[0] : undefined,
      maxPrice: localPriceRange[1] < (filterOptions?.priceRange.max || 50000) ? localPriceRange[1] : undefined
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 max-h-[90vh] flex flex-col">
        <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0">
          <DialogTitle className="sr-only">Advanced Search</DialogTitle>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search for products, categories, artisans..."
              value={filters.query}
              onChange={(e) => updateFilters({ query: e.target.value })}
              className="pl-10 pr-20 h-12 text-base border-0 border-b rounded-none focus-visible:ring-0"
              autoFocus
              maxLength={100}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {filters.query && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateFilters({ query: "" })}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 relative"
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 text-[10px] flex items-center justify-center">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Filters Panel */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <CollapsibleContent className="border-b px-4 py-3 bg-muted/30 flex-shrink-0 space-y-4">
            {/* Categories */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                <Tag className="h-3 w-3" /> Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {filterOptions?.categories.map((cat) => (
                  <Badge
                    key={cat.id}
                    variant={filters.categories.includes(cat.id) ? "default" : "outline"}
                    className="cursor-pointer hover:bg-primary/80"
                    onClick={() => handleCategoryClick(cat.id)}
                  >
                    {cat.name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Brands */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Brands
              </label>
              <div className="flex flex-wrap gap-2">
                {filterOptions?.brands.map((brand) => (
                  <Badge
                    key={brand.id}
                    variant={filters.brands.includes(brand.id) ? "default" : "outline"}
                    className="cursor-pointer hover:bg-primary/80"
                    onClick={() => handleBrandClick(brand.id)}
                  >
                    {brand.name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-3 block">
                Price: {formatPrice(localPriceRange[0])} - {formatPrice(localPriceRange[1])}
              </label>
              <Slider
                value={localPriceRange}
                min={filterOptions?.priceRange.min || 0}
                max={filterOptions?.priceRange.max || 50000}
                step={500}
                onValueChange={(value) => setLocalPriceRange(value as [number, number])}
                onValueCommit={handleApplyPriceRange}
                className="w-full"
              />
            </div>

            {/* In Stock & Sort */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inStock"
                  checked={filters.inStock}
                  onCheckedChange={(checked) => updateFilters({ inStock: !!checked })}
                />
                <label htmlFor="inStock" className="text-sm cursor-pointer">
                  In Stock Only
                </label>
              </div>

              <Select
                value={filters.sortBy}
                onValueChange={(value: typeof filters.sortBy) => updateFilters({ sortBy: value })}
              >
                <SelectTrigger className="w-40 h-8">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">Relevance</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Clear All
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Suggestions */}
        {filters.query.length >= 2 && suggestions.length > 0 && (
          <div className="px-4 py-2 border-b flex-shrink-0">
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion) => (
                <Badge
                  key={`${suggestion.type}-${suggestion.id}`}
                  variant="secondary"
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  onClick={() => handleSuggestionClick(suggestion.name)}
                >
                  <span className="text-xs text-muted-foreground mr-1">
                    {suggestion.type === "product" ? "🛍️" : suggestion.type === "category" ? "📁" : "🏢"}
                  </span>
                  {suggestion.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {searching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : results.length > 0 ? (
            <div className="p-2">
              <div className="flex items-center justify-between px-3 pb-2 border-b">
                <p className="text-xs text-muted-foreground">
                  {results.length} result{results.length !== 1 ? "s" : ""} found
                </p>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 text-xs text-primary"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/shop?search=${encodeURIComponent(filters.query)}`);
                  }}
                >
                  View in Shop
                </Button>
              </div>
              {results.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleProductClick(product.slug)}
                  className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <Search className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground truncate">
                      {product.name}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      {product.categories?.name && (
                        <Badge variant="outline" className="text-xs">
                          {product.categories.name}
                        </Badge>
                      )}
                      {product.stock_quantity === 0 && (
                        <Badge variant="destructive" className="text-xs">
                          Out of Stock
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-terracotta mt-1">
                      {formatPrice(product.price)}
                      {product.compare_at_price && product.compare_at_price > product.price && (
                        <span className="text-muted-foreground line-through ml-2 text-xs">
                          {formatPrice(product.compare_at_price)}
                        </span>
                      )}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : filters.query.length >= 2 || activeFilterCount > 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No products found</p>
              <p className="text-sm mt-1">Try a different search term or adjust filters</p>
            </div>
          ) : (
            <SearchSuggestions onSuggestionClick={handleSuggestionClick} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
